import { Component, OnInit, Signal } from '@angular/core';
import { SafeHtml } from '@angular/platform-browser';
import {
  IAuthResponse,
  IPublicBusinessConfig,
} from '@g99-product-web-ui/shared-interface-lib';
import {
  LocalStorageService,
  SanitizeService,
  SharedUserService,
  SubjectManageService,
} from '@g99-product-web-ui/shared-service';
import {
  ApiDocsIcon,
  BusinessSettingsIcon,
  ChangePasswordIcon,
  HelpCenterIcon,
  ImportExportCenterIcon,
  MessageIcon,
  MyProfileIcon,
  ReferAPracticeIcon,
  ReportsIcon,
  RoleNameEnum,
  SignOutIcon,
  SupportTicketsIcon,
  ThirdPartyIntegrationIcon,
  UserPermissionIcon,
} from '@g99-product-web-ui/shared-util-lib';

import {
  ProfileMenuLabel,
  getNavMenuItems,
  getProfileMenus,
} from './menu.config';
import { NotificationFacade } from '../../facades/notification.facade';
import { IMenuItem, IProfileMenuItem } from '../../interfaces/IMenu.interface';

@Component({
  selector: 'g99-lib-client-header',
  templateUrl: './client-header.component.html',
  styleUrl: './client-header.component.scss',
  standalone: false,
})
export class ClientHeaderComponent implements OnInit {
  public menuItems: IMenuItem[] = getNavMenuItems();
  protected businessSettingsIcon!: SafeHtml;
  protected myProfileIcon!: SafeHtml;
  protected usersPermissionsIcon!: SafeHtml;
  protected thirdPartyIntegrationIcon!: SafeHtml;
  protected apiDocsIcon!: SafeHtml;
  protected reportsIcon!: SafeHtml;
  protected helpCenterIcon!: SafeHtml;
  protected signOutIcon!: SafeHtml;
  protected referAPracticeIcon!: SafeHtml;
  protected supportTicketsIcon!: SafeHtml;
  protected profileMenus: IProfileMenuItem[] = [];
  protected businessData: IPublicBusinessConfig = {} as IPublicBusinessConfig;
  protected usersData: IAuthResponse = {} as IAuthResponse;
  protected changePasswordIcon!: SafeHtml;
  protected messageIcon!: SafeHtml;
  protected isNotificationOpen = false;
  protected isTedOpen = false;
  protected importExportIcon!: SafeHtml;
  protected hasUnseenDot: Signal<boolean>;
  protected userRole: string = RoleNameEnum.ADMIN;

  // Logo management properties
  protected logoUrl = 'assets/Logo.svg'; // Default fallback

  constructor(
    private readonly _localStorageService: LocalStorageService,
    private readonly _subjectManagerService: SubjectManageService,
    private readonly _notifications: NotificationFacade,
    private readonly _sanitizeService: SanitizeService,
    private readonly _sharedUserService: SharedUserService
  ) {
    this.hasUnseenDot = this._notifications.hasUnseenDot; // <-- wire signal
    this._notifications.setTab('all');
  }

  ngOnInit(): void {
    this._initializeIcons();
    this._getLatestChangedValues();
    this._initializeUserData();
  }

  protected get hasAdminAccess(): boolean {
    return this._sharedUserService.hasAdminAccess();
  }

  protected get isSuperAdmin(): boolean {
    return this._sharedUserService.isSupportUser();
  }

  protected onTedClick(): void {
    this.isTedOpen = !this.isTedOpen;
    if (this.isTedOpen) {
      this.isNotificationOpen = false;
      this._notifications.setPopoverOpenState(false);
    }
  }

  protected onNotificationsClick(): void {
    this.resetNotificationPopupState();
    if (this.isNotificationOpen) {
      this._notifications.clearUnseenCount();
      if (this._notifications.unseenCount() > 0) {
        this._notifications.refresh();
      }
    }
  }

  protected resetNotificationPopupState(): void {
    this.isNotificationOpen = !this.isNotificationOpen;
    this._notifications.setPopoverOpenState(this.isNotificationOpen);
  }

  // this method will get latest changed values for business data and user data to update values in header
  private _getLatestChangedValues(): void {
    this._subjectManagerService.businessSubject.subscribe((value) => {
      if (value && value.name) {
        this.businessData = { ...value };
        this._loadAgencyBasedLogoWithRetry(value);
        this.menuItems = getNavMenuItems();
      }
    });
    this._subjectManagerService.userSubject$.subscribe((value) => {
      if (value && value.firstName) {
        this.usersData.firstName = value.firstName;
        this.usersData.lastName = value.lastName;
        this.usersData.username = value.username;
        this.usersData.profileImageUrl = value.profileImageUrl;
        this.profileMenus = getProfileMenus(value.id);
        const restrictedMenus = [
          'Business Settings',
          'Users',
          'Change Password',
          'API Docs',
        ];

        // Filter menu based on role
        let role = '';
        if (value.updatedRole) {
          role = value.updatedRole.name;
        } else if (value.supportUserRoles) {
          role = value.supportUserRoles;
        } else {
          role = value.roles;
        }

        this.userRole = role;
        this.profileMenus =
          role !== RoleNameEnum.STAFF
            ? this.profileMenus // Show all menus for admin
            : this.profileMenus.filter(
                (menu) => !restrictedMenus.includes(menu.label)
              ); // Hide 3 menus for non-admins

        this._setProfileMenuIcons();
      }
    });
  }

  private _initializeIcons(): void {
    this.businessSettingsIcon =
      this._sanitizeService.sanitizeContent(BusinessSettingsIcon);
    this.myProfileIcon = this._sanitizeService.sanitizeContent(MyProfileIcon);
    this.usersPermissionsIcon =
      this._sanitizeService.sanitizeContent(UserPermissionIcon);
    this.thirdPartyIntegrationIcon = this._sanitizeService.sanitizeContent(
      ThirdPartyIntegrationIcon
    );
    this.apiDocsIcon = this._sanitizeService.sanitizeContent(ApiDocsIcon);
    this.reportsIcon = this._sanitizeService.sanitizeContent(ReportsIcon);
    this.helpCenterIcon = this._sanitizeService.sanitizeContent(HelpCenterIcon);
    this.referAPracticeIcon =
      this._sanitizeService.sanitizeContent(ReferAPracticeIcon);
    this.supportTicketsIcon =
      this._sanitizeService.sanitizeContent(SupportTicketsIcon);
    this.signOutIcon = this._sanitizeService.sanitizeContent(SignOutIcon);
    this.changePasswordIcon =
      this._sanitizeService.sanitizeContent(ChangePasswordIcon);

    this.messageIcon = this._sanitizeService.sanitizeContent(MessageIcon);
    this.importExportIcon = this._sanitizeService.sanitizeContent(
      ImportExportCenterIcon
    );
  }

  // this method will initilize user data from getting local storage
  private _initializeUserData(): void {
    const lsbusinessData =
      this._localStorageService.readItem<IPublicBusinessConfig>('businessInfo');
    const lsuserData =
      this._localStorageService.readItem<IAuthResponse>('currentUser');

    if (lsuserData) {
      this.usersData = lsuserData;
    }

    if (lsbusinessData) {
      this.businessData = lsbusinessData;
      this._loadAgencyBasedLogoWithRetry(lsbusinessData); // Initialize logo loading
    }
  }

  // this method will sets the Icons for respective
  private _setProfileMenuIcons(): void {
    this.profileMenus = this.profileMenus.map((menu) => {
      switch (menu.label) {
        case ProfileMenuLabel.BUSINESS_SETTINGS:
          menu.icon = this.businessSettingsIcon;
          break;
        case ProfileMenuLabel.MY_PROFILE:
          menu.icon = this.myProfileIcon;
          break;
        case ProfileMenuLabel.USERS_AND_PERMISSIONS:
          menu.icon = this.usersPermissionsIcon;
          break;
        case ProfileMenuLabel.THIRD_PARTY_INTEGRATIONS:
          menu.icon = this.thirdPartyIntegrationIcon;
          break;
        case ProfileMenuLabel.API_DOCS:
          menu.icon = this.apiDocsIcon;
          break;
        case ProfileMenuLabel.REFER_A_PRACTICE:
          menu.icon = this.referAPracticeIcon;
          break;
        case ProfileMenuLabel.SUPPORT_TICKETS:
          menu.icon = this.supportTicketsIcon;
          break;
        case ProfileMenuLabel.HELP_CENTER:
          menu.icon = this.helpCenterIcon;
          break;
        case ProfileMenuLabel.SIGN_OUT:
          menu.icon = this.signOutIcon;
          break;
      }
      return menu;
    });
  }

  /**
   * Loads logo from localStorage with retry mechanism
   * Attempts to fetch logo up to MAX_RETRIES times before falling back to default
   */
  /**
   * Set logo URL from business data with fallback
   */
  private _loadAgencyBasedLogoWithRetry(
    businessData: IPublicBusinessConfig
  ): void {
    this.logoUrl = businessData?.agency?.logoUrl || 'assets/Logo.svg';
  }
}
